import type { BookId, SportsbookParticipation } from '@sharpr/types';

/**
 * Project the single-source-of-truth participation map onto the two legacy sets
 * existing consumers read. `hidden` = books set to 'off'; `excluded` (from best
 * line) = books set to 'off' OR 'reference'. Unknown values are treated as
 * 'full' (defensive against stale book names after a rename or retirement) → contribute to
 * neither set.
 */
export function participationToSets(
  participation: Record<BookId, SportsbookParticipation> | null | undefined,
): { hidden: BookId[]; excluded: BookId[] } {
  const hidden: BookId[] = [];
  const excluded: BookId[] = [];
  for (const [book, level] of Object.entries(participation ?? {})) {
    if (level === 'off') {
      hidden.push(book);
      excluded.push(book);
    } else if (level === 'reference') {
      excluded.push(book);
    }
  }
  return { hidden, excluded };
}

/**
 * Resolve the visible, ordered sportsbook list given:
 *   - available: the active catalog's ids (single source of truth)
 *   - ordered:   user's custom order; items listed come first in that order
 *   - hidden:    books the user explicitly hid
 *
 * Books in `ordered` but missing from `available` are dropped (stale entries).
 * Books in `available` but absent from `ordered` append at the end in default order.
 */
export function resolveVisibleSportsbooks(
  available: readonly BookId[],
  ordered: readonly BookId[],
  hidden: readonly BookId[],
): BookId[] {
  const hiddenSet = new Set(hidden);
  const visible = available.filter(b => !hiddenSet.has(b));
  const visibleSet = new Set(visible);
  const head = ordered.filter(b => visibleSet.has(b));
  const headSet = new Set(head);
  const tail = visible.filter(b => !headSet.has(b));
  const result = [...head, ...tail];
  // Every book hidden would leave an empty board; show them all instead.
  return result.length > 0 ? result : [...available];
}
